import { EmbedLevel } from '@/common'
import Data from '@/data'
import { InventorySlotType, ItemFlags } from '@/data/enums'
import { UniqueDefinition, UniqueVariant } from '@/data/types'
import Store, { defaultPlanner, StoreState } from '@/store'
import { ProfileImported, UserEntry } from '@/types/api'
import {
  AtlasEmbed,
  BuildListEmbed,
  CraftingEmbed,
  Embed,
  PassivesEmbed,
  SkillPriorityGroup,
  TreeHistoryStep
} from '@/types/embed'
import { Gem, Item, StatType } from '@/types/item'
import { PlayerClass, Profile } from '@/types/profile'
import {
  filterArray,
  filterObject,
  filterObjectNumeric,
  isArray,
  isArrayOf,
  isBoolean,
  isNumber,
  isNumberString,
  isObject,
  isString,
  isValues,
  keyIs,
  notNull,
  removeUndefined
} from '@/utils/validation'
import { getUniqueId, validateObject } from '@maxroll-media-group/common-libs'
import { gemMaxLevel } from './gem'
import { itemCreate, itemCreateAbstract } from './item'
import { validateLevelingEmbed } from './levelingValidation'
import { parsePob } from './pob-parser/parser'

function validateStat(stat: string) {
  if (stat === 'base_number_of_additional_arrows') return 'number_of_additional_arrows'
  return stat
}

const validateStats = (stats: object) => filterObject(stats, (v) => (isNumber(v) ? v : undefined), validateStat)

function isItemOrDataId(items: StoreState['items']) {
  return (id: unknown): id is number | string => {
    if (typeof id === 'string' && Data.items[id]) return true
    if (typeof id === 'number' || typeof id === 'string') return items[Number(id)] != null
    return false
  }
}
const isPlayerClass = (val: unknown): val is PlayerClass => typeof val === 'string' && val in Data.characters

export function validateGem(gem: unknown): Gem | undefined {
  if (!isObject(gem) || !keyIs(gem, 'id', isString)) return
  let id = gem.id
  if (!Data.gems[gem.id]) {
    if (Data.gem_remap?.[id]) id = Data.gem_remap[id]
    if (!Data.gems[id]) return
  }
  return {
    id,
    level: keyIs(gem, 'level', isNumber) ? gem.level : gemMaxLevel(gem.id),
    quality: keyIs(gem, 'quality', isNumber) ? gem.quality : 20,
    corrupted: keyIs(gem, 'corrupted', isBoolean) ? gem.corrupted : false
  }
}

export function validateItem(item: unknown, items: StoreState['items']): Item | undefined {
  if (!isObject(item) || !keyIs(item, 'base', isString)) return
  const base = Data.items[item.base]
  if (!base) return

  const itemStats = <Def>(type: keyof Item['stats'], def: Def) =>
    keyIs(item, 'stats', isObject) && keyIs(item.stats, type, isObject) ? validateStats(item.stats[type]) : def
  const itemMods = <Def>(type: StatType, def: Def) =>
    keyIs(item, 'mods', isObject) && keyIs(item.mods, type, isObject)
      ? filterObject(
          item.mods[type],
          (v) => (isObject(v) ? validateStats(v) : undefined),
          (key) => (Data.mods[key] ? key : undefined)
        )
      : def

  const result: Item = {
    ...itemCreateAbstract(item.base),
    rarity: keyIs(item, 'rarity', isValues('normal', 'magic', 'rare', 'unique', 'foil')) ? item.rarity : 'normal',
    name: keyIs(item, 'name', isString) ? item.name : undefined,
    stats: removeUndefined({
      implicit: itemStats('implicit', {}),
      crafted: itemStats('crafted', {}),
      enchant: itemStats('enchant', {}),
      explicit: itemStats('explicit', {}),
      fractured: itemStats('fractured', {}),
      unique: itemStats('unique', undefined),
      custom: itemStats('custom', undefined)
    })
  }

  if (keyIs(item, 'unique', isString) && Data.uniques[item.unique]) {
    const unique = Data.uniques[item.unique]
    result.unique = item.unique
    result.variant =
      keyIs(item, 'variant', isNumber) && unique.variants[item.variant] ? item.variant : unique.variants.length - 1

    if (!result.stats.unique) {
      // Old uniques
      result.stats.unique = result.stats.explicit
      result.stats.explicit = {}
    }
  }

  if (keyIs(item, 'ilvl', isNumber)) result.ilvl = item.ilvl
  if (keyIs(item, 'map_tier', isNumber)) result.map_tier = item.map_tier
  if (keyIs(item, 'quality', isNumber)) result.quality = item.quality
  if (keyIs(item, 'flags', isNumber)) result.flags = item.flags
  if (keyIs(item, 'sockets', isArray)) {
    result.sockets = filterArray(item.sockets, (socket) => {
      if (!isObject(socket)) return
      return {
        color: keyIs(socket, 'color', isString) ? socket.color : 'red',
        link: keyIs(socket, 'link', isBoolean) ? socket.link : true,
        gem: 'gem' in socket ? validateGem(socket.gem) : undefined
      }
    })
  }
  if (keyIs(item, 'jewels', isArray)) {
    result.jewels = item.jewels.map((v) => (isNumberString(v) && items[Number(v)] ? Number(v) : undefined))
  }
  if (keyIs(item, 'properties', isObject)) {
    if (keyIs(item.properties, 'armour', isNumber)) result.properties.armour = item.properties.armour
    if (keyIs(item.properties, 'evasion', isNumber)) result.properties.evasion = item.properties.evasion
    if (keyIs(item.properties, 'energy_shield', isNumber))
      result.properties.energy_shield = item.properties.energy_shield
  }
  if (keyIs(item, 'mods', isObject)) {
    result.mods = removeUndefined({
      enchant: itemMods('enchant', {}),
      implicit: itemMods('implicit', {}),
      crafted: itemMods('crafted', undefined),
      fractured: itemMods('fractured', undefined),
      explicit: itemMods('explicit', undefined)
    })
  }
  if (keyIs(item, 'customText', isArrayOf(isString))) result.customText = item.customText
  if (keyIs(item, 'veiledMods', isNumber)) result.veiledMods = item.veiledMods
  if (keyIs(item, 'quantity', isNumber)) result.quantity = item.quantity

  if (base.cluster_jewel) {
    result.clusterJewel = {
      nodes: base.cluster_jewel.min_skills,
      skill: base.cluster_jewel.passive_skills[0].id
    }
    if (keyIs(item, 'clusterJewel', isObject)) {
      if (keyIs(item.clusterJewel, 'nodes', isNumber)) {
        result.clusterJewel.nodes = Math.min(
          base.cluster_jewel.max_skills,
          Math.max(base.cluster_jewel.min_skills, item.clusterJewel.nodes)
        )
      }
      if (keyIs(item.clusterJewel, 'skill', isString) && Data.passive_skills[item.clusterJewel.skill]) {
        result.clusterJewel.skill = item.clusterJewel.skill
      }
    }
  }

  return result
}

function validateTreeStep(step: unknown): TreeHistoryStep | undefined {
  if (isNumberString(step)) return Number(step)
  if (step && typeof step === 'object') {
    return removeUndefined({
      add: keyIs(step, 'add', isArrayOf(isNumberString)) ? step.add.map(Number) : undefined,
      remove: keyIs(step, 'remove', isArrayOf(isNumberString)) ? step.remove.map(Number) : undefined
    })
  }
}

function validateTreeEmbed(embed: object, items: StoreState['items']) {
  if (!keyIs(embed, 'variants', isArray)) return
  const variants: PassivesEmbed['variants'] = filterArray(embed.variants, (variant) => {
    if (!isObject(variant) || !keyIs(variant, 'history', isArray)) return
    const masteryDelta = keyIs(variant, 'jewels', isObject) ? 0 : -1
    return {
      name: keyIs(variant, 'name', isString) ? variant.name : 'Variant',
      history: filterArray(variant.history, validateTreeStep),
      masteries: keyIs(variant, 'masteries', isObject)
        ? filterObjectNumeric(variant.masteries, (v) => (isNumber(v) ? v + masteryDelta : undefined))
        : {},
      jewels: keyIs(variant, 'jewels', isObject)
        ? filterObjectNumeric(variant.jewels, (v) => (isNumberString(v) && items[Number(v)] ? Number(v) : undefined))
        : {},
      tattoos: keyIs(variant, 'tattoos', isObject)
        ? filterObjectNumeric(variant.tattoos, (v) => (isString(v) && Data.tattoos[v] ? v : undefined))
        : {},
      notes: keyIs(variant, 'notes', isArray)
        ? filterArray(variant.notes, (note) => {
            if (!isObject(note)) return
            return {
              text: keyIs(note, 'text', isString) ? note.text : '',
              nodes: keyIs(note, 'nodes', isArray) ? filterArray(note.nodes, (v) => (isNumber(v) ? v : undefined)) : []
            }
          })
        : []
    }
  })
  return {
    variants,
    active: keyIs(embed, 'active', isNumber) && variants[embed.active] ? embed.active : 0
  }
}

function validateAtlasEmbed(embed: object, items: StoreState['items']) {
  const tree = validateTreeEmbed(embed, items)
  if (!tree) return
  return {
    ...tree,
    strategy: keyIs(embed, 'strategy', isObject)
      ? {
          sidebar: keyIs(embed.strategy, 'sidebar', isArray)
            ? embed.strategy.sidebar.map((item) => validateMap(item, items))
            : [],
          device: keyIs(embed.strategy, 'device', isArray)
            ? embed.strategy.device.map((item) => validateMap(item, items))
            : [],
          bench: keyIs(
            embed.strategy,
            'bench',
            (val): val is string => isString(val) && Data.crafting_bench_options.find((opt) => opt.id === val) != null
          )
            ? embed.strategy.bench
            : undefined,
          sextants: keyIs(embed.strategy, 'sextants', isArray)
            ? embed.strategy.sextants.map((id) => (isString(id) && Data.mods ? id : undefined))
            : [],
          altar: keyIs(embed.strategy, 'altar', isValues(1, 2)) ? embed.strategy.altar : undefined
        }
      : undefined
  }
}

function validateSkillEmbed(embed: object, items: StoreState['items']) {
  if (!keyIs(embed, 'steps', isArray)) return
  return {
    steps: filterArray(embed.steps, (step, index) => {
      if (!isObject(step) || !keyIs(step, 'skills', isArray)) return
      return {
        name: keyIs(step, 'name', isString) ? step.name : `Step ${index}`,
        skills: filterArray(step.skills, (skill) => {
          if (!isObject(skill)) return
          return {
            item: keyIs(skill, 'item', isItemOrDataId(items))
              ? Data.items[skill.item]
                ? String(skill.item)
                : Number(skill.item)
              : undefined,
            type: keyIs(skill, 'type', isNumber) ? skill.type : undefined,
            slot: keyIs(skill, 'slot', isString) ? (skill.slot as InventorySlotType) : undefined,
            gems: keyIs(skill, 'gems', isArray) ? filterArray(skill.gems, validateGem) : [],
            unlink: keyIs(skill, 'unlink', isArrayOf(isBoolean)) ? skill.unlink : undefined
          }
        })
      }
    }),
    step: keyIs(embed, 'step', isNumber) ? embed.step : 0
  }
}

function validateMap(map: unknown, items: StoreState['items']) {
  if (typeof map === 'string') return itemCreate(map)
  return validateItem(map, items)
}

function validatePriorityGroup(group: unknown): SkillPriorityGroup | undefined {
  if (!isObject(group)) return
  if (!keyIs(group, 'gems', isArray)) return
  return {
    gems: filterArray(group.gems, validateGem),
    text: keyIs(group, 'text', isString) ? group.text : undefined
  }
}

export function validateEmbed(embed: unknown, items: StoreState['items']): Embed | undefined {
  if (!isObject(embed) || !keyIs(embed, 'type', isString)) return
  if (!keyIs(embed, 'id', isNumberString)) return
  const base = {
    id: Number(embed.id),
    name: keyIs(embed, 'name', isString) ? embed.name : 'Embed'
  }
  switch (embed.type) {
    case 'equipment':
      if (!keyIs(embed, 'items', isObject)) return
      return {
        type: 'equipment',
        ...base,
        items: filterObject(embed.items, (v) => (isNumberString(v) && items[Number(v)] ? Number(v) : undefined))
      }
    case 'pantheon':
      if (!keyIs(embed, 'active', isObject)) return
      return {
        type: 'pantheon',
        ...base,
        active: filterObject(
          embed.active,
          (v) => (isArrayOf(isBoolean)(v) ? v : undefined),
          (k) => (Data.pantheon[k] ? k : undefined)
        )
      }
    case 'skills':
    case 'skills-video-display': {
      const skills = validateSkillEmbed(embed, items)
      if (!skills) return
      return {
        type: embed.type,
        ...base,
        ...skills
      }
    }
    case 'link':
      if (!keyIs(embed, 'gems', isArray)) return
      return {
        type: 'link',
        ...base,
        gems: filterArray(embed.gems, validateGem)
      }
    case 'ascendancy':
      if (!keyIs(embed, 'ascendancy', isString) || !Data.ascendancyClass[embed.ascendancy]) return
      if (!keyIs(embed, 'steps', isArray)) return
      return {
        type: 'ascendancy',
        ...base,
        version: keyIs(embed, 'version', isNumber) ? embed.version : Data.defaultVersion,
        ascendancy: embed.ascendancy,
        steps: filterArray(embed.steps, (step) => (isArray(step) ? filterArray(step, validateTreeStep) : undefined))
      }
    case 'passives': {
      if (!keyIs(embed, 'charClass', isPlayerClass)) return
      const passiveTree = validateTreeEmbed(embed, items)
      if (!passiveTree) return
      return {
        type: 'passives',
        ...base,
        charClass: embed.charClass,
        // default to 318 because only oldest embeds are missing this field
        version: keyIs(embed, 'version', isNumber) ? embed.version : 318,
        ascendancy:
          keyIs(embed, 'ascendancy', isString) && Data.characters[embed.charClass].ascendancies[embed.ascendancy]
            ? embed.ascendancy
            : undefined,
        ...passiveTree
      }
    }
    case 'atlas': {
      const atlasTree = validateAtlasEmbed(embed, items)
      if (!atlasTree) return
      return {
        type: 'atlas',
        league: keyIs(embed, 'league', isBoolean) ? embed.league : undefined,
        ...base,
        // default to 318 because only oldest embeds are missing this field
        version: keyIs(embed, 'version', isNumber) ? embed.version : 318,
        ...atlasTree
      }
    }
    case 'leveling':
      return validateLevelingEmbed(embed, items)
    case 'crafting':
      return embed as CraftingEmbed
    case 'buildlist':
      return embed as BuildListEmbed
  }
}

export function validateAuthor(data: unknown, user?: UserEntry): Profile['author'] | undefined {
  if (!isObject(data)) return
  return {
    contentCreator:
      user && user.active >= EmbedLevel && keyIs(data, 'contentCreator', isString) ? data.contentCreator : undefined,
    user:
      keyIs(data, 'user', isObject) && keyIs(data.user, 'nickname', isString)
        ? {
            nickname: data.user.nickname,
            social: keyIs(data.user, 'social', isObject)
              ? validateObject(data.user.social, {
                  twitch: isString,
                  youtube: isString,
                  twitter: isString
                })
              : undefined
          }
        : undefined
  }
}

function validateEquipment(equipment: unknown, items: StoreState['items']): Profile['equipment'] | undefined {
  if (!isObject(equipment) || !keyIs(equipment, 'variants', isArray)) return
  const result: Profile['equipment'] = {
    variants: filterArray(equipment.variants, (variant) => {
      if (!isObject(variant) || !keyIs(variant, 'items', isObject)) return
      return {
        id: keyIs(variant, 'id', isString) ? variant.id : getUniqueId(),
        name: keyIs(variant, 'name', isString) ? variant.name : undefined,
        items: filterObject(variant.items, (v) => (isNumberString(v) && items[Number(v)] ? Number(v) : undefined)),
        priority: keyIs(variant, 'priority', isObject)
          ? filterObject(variant.priority, (list) => {
              if (!isArray(list)) return
              return filterArray(list, (v) => {
                if (!isObject(v) || !keyIs(v, 'id', isString)) return
                return {
                  id: validateStat(v.id),
                  rank: keyIs(v, 'rank', isNumber) ? v.rank : 0
                }
              })
            })
          : undefined
      }
    }),
    active: 0
  }
  if (!result.variants.length) {
    result.variants.push({
      id: getUniqueId(),
      items: {},
      name: 'Default'
    })
  }
  if (keyIs(equipment, 'active', isNumber) && result.variants[equipment.active]) {
    result.active = equipment.active
  }
  return result
}

export function validatePlanner(planner: unknown, items: StoreState['items'], user?: UserEntry) {
  //TODO: when adding profile fields, fix this
  if (!isObject(planner)) return
  if (!keyIs(planner, 'class', isPlayerClass)) return
  if (!keyIs(planner, 'version', isNumber) || !Data.versions.includes(planner.version)) return

  const result = defaultPlanner(planner.class)
  result.version = planner.version

  if (keyIs(planner, 'level', isNumber)) {
    result.level = planner.level
  }
  if (keyIs(planner, 'ascendancy', isString)) {
    if (Data.characters[result.class].ascendancies[planner.ascendancy]) {
      result.ascendancy = planner.ascendancy
    }
  }

  if ('equipment' in planner) {
    result.equipment = validateEquipment(planner.equipment, items) ?? result.equipment
  }
  if ('mercenary' in planner) {
    result.mercenary = validateEquipment(planner.mercenary, items) ?? result.mercenary
  }

  if (keyIs(planner, 'passives', isObject)) {
    result.passives = validateTreeEmbed(planner.passives, items) ?? result.passives
  }

  if (keyIs(planner, 'atlas', isObject)) {
    result.atlas = validateAtlasEmbed(planner.atlas, items) ?? result.atlas
  }

  if (keyIs(planner, 'skills', isObject)) {
    result.skills = validateSkillEmbed(planner.skills, items) ?? result.skills
  }

  if (keyIs(planner, 'bandits', isString)) {
    result.bandits = planner.bandits
  }

  if (keyIs(planner, 'pantheon', isObject)) {
    result.pantheon = filterObject(
      planner.pantheon,
      (v) => (isArrayOf(isBoolean)(v) ? v : undefined),
      (k) => (Data.pantheon[k] ? k : undefined)
    )
  }

  if (keyIs(planner, 'showcaseVideo', isString)) {
    result.showcaseVideo = planner.showcaseVideo
  }

  if (keyIs(planner, 'relatedBuild', isString)) {
    result.relatedBuild = planner.relatedBuild
  }

  if (keyIs(planner, 'strAndWeak', isObject)) {
    result.strAndWeak = {
      strengths: keyIs(planner.strAndWeak, 'strengths', isArray) ? planner.strAndWeak.strengths.filter(isString) : [],
      weaknesses: keyIs(planner.strAndWeak, 'weaknesses', isArray) ? planner.strAndWeak.weaknesses.filter(isString) : []
    }
  }

  if (keyIs(planner, 'buildRating', isObject) && keyIs(planner.buildRating, 'ratings', isObject)) {
    result.buildRating = {
      ratings: Object.entries(planner.buildRating.ratings)
        .filter(([key, value]) => isString(key) && isNumber(value))
        .reduce((acc, [key, val]) => {
          acc[key] = val
          return acc
        }, {} as Record<string, number>)
    }
  }

  if ('notes' in planner) {
    result.notes = planner.notes
  }

  if ('changelog' in planner) {
    result.changelog = planner.changelog
  }

  if ('author' in planner) {
    result.author = validateAuthor(planner.author, user)
  }

  if (keyIs(planner, 'skillRotations', isArray)) {
    result.skillRotations = filterArray(planner.skillRotations, (val) =>
      isObject(val) && keyIs(val, 'name', isString) && keyIs(val, 'groups', isArray)
        ? {
            ...val,
            id: keyIs(val, 'id', isString) ? val.id : getUniqueId(),
            groups: filterArray(val.groups, validatePriorityGroup)
          }
        : undefined
    )

    const existingIds: string[] = []
    for (const variant of result.skillRotations) {
      if (existingIds.includes(variant.id)) {
        variant.id = getUniqueId()
      } else {
        existingIds.push(variant.id)
      }
    }
  }

  if (keyIs(planner, 'lootfilterLinks', isArray)) {
    result.lootfilterLinks = filterArray(planner.lootfilterLinks, (lootfilterLink) =>
      isObject(lootfilterLink) && keyIs(lootfilterLink, 'link', isString)
        ? {
            name: keyIs(lootfilterLink, 'name', isString) ? lootfilterLink.name : '',
            link: lootfilterLink.link
          }
        : undefined
    )
  }

  if (keyIs(planner, 'pobProfileId', isString)) {
    result.pobProfileId = planner.pobProfileId
  }

  return result
}

export async function validateState(state: unknown, imported?: ProfileImported) {
  const result: StoreState = {
    ...Store.initialState,
    id: Store.newId(),
    imported
  }
  if (isObject(state)) {
    if (keyIs(state, 'pobCode', isString)) {
      try {
        return {
          ...(await parsePob(state.pobCode)),
          imported
        }
      } catch (e) {
        return result
      }
    }

    if (keyIs(state, 'items', isObject)) {
      result.items = filterObjectNumeric(state.items, (item) => validateItem(item, state.items))
    }
    if (keyIs(state, 'embeds', isArray)) {
      result.embeds = filterArray(state.embeds, (embed) => validateEmbed(embed, result.items))
    }
    if (keyIs(state, 'activeEmbed', isNumber)) {
      result.activeEmbed = state.activeEmbed
    }
    if ('planner' in state) {
      result.planner = validatePlanner(state.planner, result.items, imported?.user)
    }
    if (keyIs(state, 'passive_tree', isObject) && keyIs(state.passive_tree, 'type', isValues('passives', 'atlas'))) {
      result.passive_tree = validateEmbed({ id: 0, ...state.passive_tree }, result.items) as PassivesEmbed | AtlasEmbed
    }
    if (!imported && keyIs(state, 'imported', isObject) && keyIs(state.imported, 'id', isString)) {
      result.imported = {
        name: '',
        folder: 0,
        id: state.imported.id,
        average_rating: 0,
        total_ratings: 0,
        date: 0,
        ...validateObject(state.imported, {
          name: isString,
          class: isString,
          category: isString,
          tags: isString,
          mainset: isString,
          metadata: isString,
          public: isNumber,
          season: isString,
          type: isString,
          folder: isNumber,
          id: isString,
          average_rating: isNumber,
          total_ratings: isNumber,
          userRating: isNumber,
          user: (src: unknown): src is UserEntry =>
            isObject(src) &&
            keyIs(src, 'id', isNumber) &&
            keyIs(src, 'username', isString) &&
            keyIs(src, 'active', isNumber),
          date: isNumber,
          modified: isValues('heavy', 'light')
        })
      }
    }
  }
  if (!result.embeds[result.activeEmbed]) {
    result.activeEmbed = -1
  }
  return result
}

// we have to be more lenient here, as it could break unique DB
// it's better to crash the tool than overwrite uniques
export function validateUniques(state: unknown) {
  if (!isObject(state)) return {}
  return filterObject<UniqueDefinition>(state, (unique) => {
    if (!isObject(unique) || !keyIs(unique, 'id', isString) || !keyIs(unique, 'name', isString)) return
    const result = { ...unique } as UniqueDefinition
    if (!keyIs(unique, 'flags', isNumber)) {
      result.flags = 'corrupted' in result && result.corrupted ? ItemFlags.Corrupted : 0
      if ('corrupted' in result) delete result.corrupted
    }
    if (keyIs(unique, 'icon', isString)) result.icon = result.icon.toLowerCase()
    result.variants = (unique as any).variants
      .map((variant: unknown): UniqueVariant | undefined => {
        if (!isObject(variant)) return
        const stats = keyIs(variant, 'stats', isObject)
          ? filterObject(
              variant.stats,
              (v) => {
                if (isNumber(v)) return v
                if (isObject(v)) {
                  return {
                    min: keyIs(v, 'min', isNumber) ? v.min : 0,
                    max: keyIs(v, 'max', isNumber) ? v.max : 0
                  }
                }
              },
              validateStat
            )
          : {}
        delete stats.number_of_stackable_unique_jewels
        return {
          ...(variant as UniqueVariant),
          stats
        }
      })
      .filter(notNull)
    return result
  })
}
